import React, { useState, useEffect } from 'react';
import { HelpCircle, X, Save } from 'lucide-react';

const HelpButton = ({ pageId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extraire l'ID de la vidéo YouTube depuis l'URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Charger le tutoriel existant
  useEffect(() => {
    if (isOpen) {
      loadTutorial();
    }
  }, [isOpen, pageId]);

  const loadTutorial = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials/${pageId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setYoutubeUrl(data.youtube_url || '');
        setTitle(data.title || '');
      }
    } catch (error) {
      console.error('Erreur chargement tutoriel:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTutorial = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tutorials/${pageId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            youtube_url: youtubeUrl,
            title: title
          })
        }
      );
      
      if (response.ok) {
        alert('✅ Tutoriel sauvegardé avec succès !');
        setIsEditing(false);
      } else {
        alert('❌ Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde tutoriel:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const videoId = getYoutubeVideoId(youtubeUrl);

  return (
    <>
      {/* Bouton d'aide flottant */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center text-white"
        style={{ backgroundColor: 'rgb(196, 150, 96)' }}
        title="Tutoriel vidéo"
      >
        <HelpCircle className="w-7 h-7" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b" style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
              <h2 className="text-xl font-semibold text-white flex items-center">
                <HelpCircle className="w-6 h-6 mr-2" />
                Tutoriel - {title || 'Cette page'}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'rgb(196, 150, 96)' }}></div>
                </div>
              ) : (
                <>
                  {/* Vidéo YouTube */}
                  {videoId && !isEditing ? (
                    <div className="mb-6">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="Tutoriel YouTube"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  ) : !videoId && !isEditing ? (
                    <div className="text-center py-12 text-gray-500">
                      <HelpCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg mb-2">Aucun tutoriel disponible pour cette page</p>
                      <p className="text-sm">Cliquez sur "Modifier" pour ajouter une vidéo YouTube</p>
                    </div>
                  ) : null}

                  {/* Formulaire d'édition */}
                  {isEditing && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Titre du tutoriel (optionnel)
                        </label>
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Ex: Comment gérer les membres"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                          style={{ focusRingColor: 'rgb(196, 150, 96)' }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL de la vidéo YouTube *
                        </label>
                        <input
                          type="url"
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                          style={{ focusRingColor: 'rgb(196, 150, 96)' }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Formats acceptés: youtube.com/watch?v=... ou youtu.be/...
                        </p>
                      </div>

                      {/* Aperçu */}
                      {getYoutubeVideoId(youtubeUrl) && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Aperçu :</p>
                          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                            <iframe
                              className="absolute top-0 left-0 w-full h-full rounded-lg"
                              src={`https://www.youtube.com/embed/${getYoutubeVideoId(youtubeUrl)}`}
                              title="Aperçu"
                              frameBorder="0"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md transition"
              >
                Fermer
              </button>

              <div className="flex space-x-2">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-white rounded-md transition hover:opacity-90"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    {youtubeUrl ? 'Modifier' : 'Ajouter un tutoriel'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        loadTutorial(); // Recharger les données originales
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={saveTutorial}
                      disabled={!youtubeUrl || loading}
                      className="px-4 py-2 text-white rounded-md transition hover:opacity-90 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Sauvegarder
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;
